import { PerspectiveAttribute, PerspectiveValueType } from '@the-real-insight/tri-model';

import { monthsOf } from './durationValue';

/**
 * What happens when a field is left.
 *
 * The form has no Save button, so leaving a field is the whole of the decision: there is no later
 * moment at which the change becomes real, and nothing is held back waiting for one. Three outcomes
 * and no fourth:
 *
 *  - **none** — nothing changed. Tabbing through a form must not write anything, and re-typing the
 *    figure that was already there is not a change however it was spelled.
 *  - **write** — the field was empty and now is not. A first entry needs no defence, and asking for
 *    one would put a dialog in front of somebody simply filling in a form.
 *  - **ask** — something that was already there is being replaced or cleared. That is the moment a
 *    reason is worth having, and the only one: the reason for a value nobody has questioned is
 *    usually "because that is what it is".
 *
 * The comparison is on the *written* form rather than the raw value, because that is the only
 * comparison that holds across the types in play: 500 and "500" are one figure, an ISO day and the
 * Date it parses to are one day, and a numeric field that hands back a number after being filled
 * from a string would otherwise look changed every single time it was left.
 */

export type CommitDecision = 'none' | 'write' | 'ask';

export const isBlank = (value: any): boolean =>
    value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

/** What an input shows for a stored value. Dates need the ISO day the date input speaks. */
export const toInputValue = (attribute: Pick<PerspectiveAttribute, 'valueType'>, raw: any): string => {
    if (isBlank(raw)) return '';

    if (attribute.valueType === PerspectiveValueType.date) {
        const parsed = new Date(raw);
        return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
    }

    if (attribute.valueType === PerspectiveValueType.boolean) {
        return String(raw);
    }

    // "P1Y" and "P12M" are the same span; compared as months, re-entering 12 is no change.
    if (attribute.valueType === PerspectiveValueType.duration) {
        const months = monthsOf(raw);
        return months === null ? String(raw) : String(months);
    }

    return String(raw);
};

export const decideCommit = (
    attribute: Pick<PerspectiveAttribute, 'valueType'>,
    previous: any,
    next: any,
): CommitDecision => {
    if (toInputValue(attribute, previous) === toInputValue(attribute, next)) return 'none';

    return isBlank(previous) ? 'write' : 'ask';
};
